import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { act, type Action, ApiError, api, type Organisation, type SessionView } from '../api/client';
import { Header } from '../components/Header';
import { PrivacyLine } from '../components/PrivacyLine';
import { type Key, pick, translator } from '../i18n';
import { setPageIdentity, trustHref } from '../page';
import { MeasureField, PageQuestions, type Value } from './Page';
import { Message } from './Start';

type Props = { resumeToken: string | null; resumeInvalid: boolean };
/** `summarising`: the reply is done and a summary to confirm is being written (the server said so at turn end). */
type Streaming = { text: string; summarising?: boolean } | null;

/** How long an action may run before "Writing…" shows: most that write nothing (a page change) are done by then. */
export const TYPING_DELAY_MS = 500;
/** Within this many pixels of the bottom, the participant is following along and new text keeps in view. */
const FOLLOW_PX = 120;

/** True once `on` has held for `ms`; false as soon as it does not. */
function useDelayed(on: boolean, ms: number) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!on) return setShown(false);
    const timer = setTimeout(() => setShown(true), ms);
    return () => clearTimeout(timer);
  }, [on, ms]);
  return shown;
}

/**
 * One session screen for every survey (ADR 0002): the current step is a page of measures, a
 * follow-up on one of them, or a conversation with checkpoints, pause and stop (SPEC §12, Participant).
 */
export function Session({ resumeToken, resumeInvalid }: Props) {
  const [view, setView] = useState<SessionView | null>(null);
  const [org, setOrg] = useState<Organisation | null>(null);
  const [missing, setMissing] = useState(false);
  const [streaming, setStreaming] = useState<Streaming>(null);
  const [error, setError] = useState<Key | null>(null);
  const [draft, setDraft] = useState('');
  const [announce, setAnnounce] = useState('');
  const [paused, setPaused] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  /** Answers taken back by going back, by measure id, to show on that page again. */
  const [prefill, setPrefill] = useState<Record<string, Value>>({});
  /** The follow-up whose answer the participant is changing (`canChange`); another one closes it. */
  const [changingId, setChangingId] = useState<string | null>(null);
  /** The follow-up or conversation phase already opened, so each is opened once. */
  const opened = useRef<string | null>(null);
  /** Whether the participant is at the bottom of the conversation; reading further up, they stay put. */
  const following = useRef(true);
  const typing = useDelayed(!!streaming && !streaming.text, TYPING_DELAY_MS);

  const refresh = useCallback(async () => {
    const { data } = await api.GET('/api/p/sessions/current');
    if (data) setView(data);
    else {
      // No session: speak the organisation's language and show its brand.
      const o = await api.GET('/api/p/organisation');
      if (o.data) setOrg(o.data);
      setMissing(true);
    }
    return data ?? null;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const t = translator(view?.locale ?? org?.defaultLocale ?? navigator.language.slice(0, 2));
  /** The current translator for `run`, which is made once. */
  const tNow = useRef(t);
  tNow.current = t;
  useEffect(() => {
    if (!view) return;
    document.documentElement.lang = view.locale;
    setPageIdentity(`${pick(view.title, view.locale)} – ${view.branding.displayName}`, view.branding.logo);
  }, [view?.locale, view?.surveySlug]);

  const run = useCallback(
    async (action: Action): Promise<boolean> => {
      setError(null);
      setStreaming({ text: '' });
      try {
        await act(action, (e) => {
          if (e.type === 'text') setStreaming((s) => ({ ...s, text: (s?.text ?? '') + e.delta }));
          if (e.type === 'turn_end' && e.decision === 'checkpoint') {
            setStreaming((s) => ({ text: s?.text ?? '', summarising: true }));
            setAnnounce(tNow.current('summarising'));
          }
        });
        const next = await refresh();
        const last = next?.messages.at(-1);
        // Streaming text is not announced token by token; the completed message is (SPEC §12), with a
        // measure's question when one is shown under it.
        if (last?.role === 'assistant') setAnnounce(last.widget?.type === 'measure' ? `${last.content} ${pick(last.widget.question.text, next!.locale)}` : last.content);
        return true;
      } catch (e) {
        const code = e instanceof ApiError ? e.code : 'generic';
        setError((['busy', 'unavailable', 'conflict', 'closed', 'rate_limited'].includes(code) ? `error.${code}` : 'error.generic') as Key);
        if (code === 'conflict') await refresh();
        return false;
      } finally {
        setStreaming(null);
      }
    },
    [refresh],
  );

  // A follow-up or a conversation phase opens with the AI's first question.
  const openKey = view?.step && !view.started && view.status === 'in_progress' ? `${view.step.id}:${view.step.followUp?.questionId ?? ''}` : null;
  useEffect(() => {
    if (openKey && opened.current !== openKey) {
      opened.current = openKey;
      void run({ type: 'open' });
    }
  }, [openKey, run]);

  useEffect(() => {
    const onScroll = () => {
      following.current = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - FOLLOW_PX;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // New and streaming text would otherwise end up behind the sticky answer box (MEASURED 2026-11-02, a
  // long answer left the next question 240 px below it). The page's end is just above the box.
  const lastText = view?.messages.at(-1)?.content;
  useEffect(() => {
    // A summary takes focus and is scrolled to its heading instead (below).
    if (following.current && view?.status === 'in_progress' && view.step?.type !== 'page') window.scrollTo({ top: document.documentElement.scrollHeight });
  }, [view?.messages.length, lastText, streaming?.text, typing]);

  // A page's follow-ups read as one thread: an answered one stays above the next instead of the screen
  // being replaced (2026-11-02, to try out). The server sends only the current follow-up, so the earlier
  // ones are kept here, for this page, until it changes; after a reload the thread starts at the current one.
  const [earlier, setEarlier] = useState<SessionView[]>([]);
  const shownFollowUp = useRef<SessionView | null>(null);
  useEffect(() => {
    if (!view) return;
    const prev = shownFollowUp.current;
    const now = view.step?.type === 'follow_up' ? view : null;
    if (now && prev && prev.step!.id === now.step!.id) {
      // Moved on to the next follow-up on the same page: keep the last one if it was answered, not skipped.
      if (prev.step!.followUp!.questionId !== now.step!.followUp!.questionId && prev.messages.some((m) => m.role === 'user')) setEarlier((e) => [...e, prev]);
    } else setEarlier([]);
    shownFollowUp.current = now;
  }, [view]);

  if (missing) {
    if (org) document.documentElement.lang = org.defaultLocale;
    return <Message text={t(resumeInvalid ? 'resumeInvalid' : 'noSession')} />;
  }
  if (!view) return <Message text={t('loading')} />;

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    setView({ ...view, messages: [...view.messages, { role: 'user', content: trimmed, widget: null, skipped: false }] });
    following.current = true; // Sending is following along, wherever the participant had scrolled.
    setDraft('');
    // On failure the text goes back into the box so nothing is lost.
    if (!(await run({ type: 'message', text: trimmed }))) {
      setDraft(trimmed);
      await refresh();
    }
  };

  /**
   * The follow-up's answer changed (once): shown at once, then the follow-up starts over about it, or the
   * next one comes if it no longer applies (the server decides).
   */
  const change = async (value: Value) => {
    const s = view.step!;
    setChangingId(null);
    setView({ ...view, step: { ...s, followUp: { ...s.followUp!, answer: value, canChange: false } } });
    following.current = true;
    await run({ type: 'answer', measureId: s.followUp!.questionId, value });
  };

  /** Back to the previous page; its answers come back from the server to be shown again. */
  const back = async (stepId: string) => {
    const { data } = await api.POST('/api/p/sessions/current/back', { body: { step: stepId } });
    if (data) setPrefill((p) => ({ ...p, ...data.answers }));
    await refresh();
  };

  const stop = async () => {
    await api.POST('/api/p/sessions/current/stop');
    setConfirmStop(false);
    await refresh();
  };

  const style = { '--brand': view.branding.colours.primary, '--on-brand': view.branding.colours.onPrimary ?? '#FFFFFF' };
  // What is shown under the interviewer's last message: a measure to answer, or quick replies (ADR 0002).
  const lastWidget = !streaming && view.status === 'in_progress' && view.messages.at(-1)?.role === 'assistant' ? view.messages.at(-1)!.widget : null;
  const resumeUrl = resumeToken ? `${location.origin}/r/${resumeToken}` : null;
  const active = view.status === 'in_progress' || view.status === 'checkpoint';
  const step = view.status === 'in_progress' ? view.step : null;
  const changing = !!step?.followUp && changingId === step.followUp.questionId;

  return (
    <div class="page" style={style}>
      <Header
        branding={view.branding}
        progress={view.progress}
        locale={view.locale}
        t={t}
        trustSlug={view.surveySlug}
        // A recurring survey names its round (visual draft); pausing is offered in conversations.
        context={view.round ? t('roundLabel', { n: view.round.number, when: roundDate(view.round, view.locale) }) : undefined}
        onPause={active && view.step?.type === 'conversation' ? () => setPaused(true) : undefined}
      />
      <div class="visually-hidden" aria-live="polite">
        {announce}
      </div>

      {paused && <PausePanel t={t} resumeUrl={resumeUrl} personal={view.linkKind === 'personal'} onClose={() => setPaused(false)} />}

      {view.status === 'completed' && <EndPanel title={t('doneTitle')} body={t('doneBody')} trust={{ href: trustHref(view.surveySlug, view.locale), text: t('doneTrust') }} />}
      {view.status === 'stopped' && <EndPanel title={t('stoppedTitle')} body={t('stoppedBody')} trust={{ href: trustHref(view.surveySlug, view.locale), text: t('doneTrust') }} />}

      {view.status === 'checkpoint' && view.checkpoint && (
        // The summary comes as a card at the end of the conversation it sums up, not as a new screen
        // (2026-11-02, to try out): the closing reply was replaced by it after a few seconds (MEASURED).
        <main class="conversation">
          {view.messages.length > 0 && (
            <ol class="messages" aria-label={t('conversation')}>
              <Bubbles view={view} t={t} />
            </ol>
          )}
          <Checkpoint view={view} t={t} busy={!!streaming} error={error} run={run} />
        </main>
      )}

      {step?.type === 'page' && (
        // Keyed by step: a new page starts empty and takes focus.
        <PageQuestions
          key={step.id}
          title={pick(view.title, view.locale)}
          stepId={step.id}
          questions={step.questions}
          locale={view.locale}
          t={t}
          initial={Object.fromEntries(step.questions.filter((q) => prefill[q.id] !== undefined).map((q) => [q.id, prefill[q.id]!]))}
          onSubmitted={() => void refresh()}
          onBack={step.canGoBack ? () => void back(step.id) : undefined}
        />
      )}

      {step && step.type !== 'page' && (
        <>
          <main class="conversation">
            {step.type === 'follow_up' && <h1 class="visually-hidden">{t('followUpTitle')}</h1>}
            {step.type === 'follow_up' &&
              earlier.map((v) => (
                <section key={v.step!.followUp!.questionId} class="thread-part">
                  <FollowUpContext view={v} t={t} />
                  <ol class="messages" aria-label={t('conversation')}>
                    <Bubbles view={v} t={t} />
                  </ol>
                </section>
              ))}
            {step.type === 'follow_up' && (
              <FollowUpContext view={view} t={t} onChange={step.followUp!.canChange && !streaming && !changing ? () => setChangingId(step.followUp!.questionId) : undefined} />
            )}
            {step.type === 'follow_up' && changing && (
              // The answer shown again with the current one selected, as a reopened measure in a conversation.
              <MeasureInChat
                key={step.followUp!.questionId}
                widget={{ type: 'measure', question: step.questions[0]!, required: true, reopen: true, current: step.followUp!.answer }}
                locale={view.locale}
                t={t}
                onAnswer={(value) => value !== null && void change(value)}
                onCancel={() => {
                  setChangingId(null);
                  // Back to where the participant came from, not to the top of the page.
                  requestAnimationFrame(() => document.getElementById('change-answer')?.focus());
                }}
              />
            )}
            <ol class="messages" aria-label={t('conversation')}>
              <Bubbles view={view} t={t} />
              {streaming && (streaming.text || (typing && !streaming.summarising)) && (
                <li class="bubble is-assistant" aria-busy="true">
                  <span class="visually-hidden">{t('interviewer')} </span>
                  {streaming.text || <span class="muted">{t('writing')}</span>}
                </li>
              )}
              {streaming?.summarising && (
                <li class="summarising muted" aria-busy="true">
                  {t('summarising')}
                </li>
              )}
            </ol>
            {lastWidget?.type === 'measure' && (
              <MeasureInChat key={lastWidget.question.id} widget={lastWidget} locale={view.locale} t={t} onAnswer={(value) => void run({ type: 'answer', measureId: lastWidget.question.id, value })} />
            )}
            {lastWidget?.type === 'replies' && (
              // Buttons alone read as the only answers; the hint says the box works too (MEASURED 2026-11-02).
              <div class="quick-replies-block">
                <p id="quick-replies-hint" class="muted">
                  {t('quickRepliesHint')}
                </p>
                <div class="quick-replies" role="group" aria-label={t('quickReplies')} aria-describedby="quick-replies-hint">
                  {lastWidget.options.map((o) => (
                    <button key={o} type="button" class="button-secondary" onClick={() => void send(o)}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </main>
          <footer class="composer">
            {error && (
              <p class="error" role="alert">
                {t(error)}
              </p>
            )}
            <form
              class="composer-row"
              onSubmit={(e) => {
                e.preventDefault();
                void send(draft);
              }}
            >
              <label for="answer" class="visually-hidden">
                {t('answerLabel')}
              </label>
              <textarea
                id="answer"
                rows={2}
                placeholder={t('answerPlaceholder')}
                value={draft}
                onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send(draft);
                  }
                }}
              />
              <button type="submit" class="send-button" aria-label={t('send')} disabled={!!streaming || !draft.trim()}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M5 12h14" />
                  <path d="M13 6l6 6-6 6" />
                </svg>
              </button>
            </form>
            <div class="composer-meta">
              <PrivacyLine level={view.privacyLevel} t={t} />
              {step.type === 'follow_up' ? (
                <button type="button" class="link-button" disabled={!!streaming} onClick={() => void run({ type: 'skip' })}>
                  {t('skip')}
                </button>
              ) : confirmStop ? (
                <div class="stop-confirm" role="group" aria-label={t('stop')}>
                  <p>{t('stopConfirm')}</p>
                  <button type="button" class="button-secondary" onClick={() => void stop()}>
                    {t('stopYes')}
                  </button>
                  <button type="button" class="button-secondary" onClick={() => setConfirmStop(false)}>
                    {t('stopNo')}
                  </button>
                </div>
              ) : (
                <div class="composer-actions">
                  {/* A conversation that a condition started can be declined as a whole (ADR 0002: a way out, always). */}
                  {step.skippable && (
                    <button type="button" class="link-button" disabled={!!streaming} onClick={() => void run({ type: 'skip' })}>
                      {t('declineConversation')}
                    </button>
                  )}
                  <button type="button" class="link-button" onClick={() => setConfirmStop(true)}>
                    {t('stop')}
                  </button>
                </div>
              )}
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

/** A follow-up's or conversation's messages as list items. */
function Bubbles({ view, t }: { view: SessionView; t: ReturnType<typeof translator> }) {
  return (
    <>
      {view.messages.map((m, i) => (
        <li key={i} class={`bubble is-${m.role}`}>
          <span class="visually-hidden">{m.role === 'assistant' ? t('interviewer') : t('you')} </span>
          {m.skipped ? <span class="muted">{t('measureSkipped')}</span> : m.content}
          {/* An answered measure stays in the transcript as the question it was. */}
          {m.widget?.type === 'measure' && i < view.messages.length - 1 && <span class="bubble-measure">{pick(m.widget.question.text, view.locale)}</span>}
        </li>
      ))}
    </>
  );
}

function Checkpoint({
  view,
  t,
  busy,
  error,
  run,
}: {
  view: SessionView;
  t: ReturnType<typeof translator>;
  busy: boolean;
  error: Key | null;
  run: (a: Action) => Promise<boolean>;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [correcting, setCorrecting] = useState(false);
  const [correction, setCorrection] = useState('');
  /** A correction is being written into a new summary; confirming keeps the summary on screen. */
  const [rewriting, setRewriting] = useState(false);
  const cp = view.checkpoint!;
  // A conversation phase, or a follow-up with a checkpoint: then the measure it followed up is the title.
  const title = view.phases.find((p) => p.id === cp.phase)?.title ?? view.step?.questions.find((q) => q.id === cp.phase)?.text;

  // Focus moves to the checkpoint heading when it appears (SPEC §12).
  // Its top, not the page's end, comes into view: under a conversation the heading was scrolled off (MEASURED).
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    heading.current?.parentElement?.scrollIntoView?.({ block: 'start' });
  }, [cp.phase, cp.summary]);

  return (
    <section class="checkpoint" aria-labelledby="checkpoint-title">
      <p class="eyebrow">{t('checkpointLabel')}</p>
      <h1 id="checkpoint-title" ref={heading} tabIndex={-1}>
        {t('checkpointTitle')}
      </h1>
      <p class="muted">{t('checkpointIntro')}</p>
      <section class="summary" aria-labelledby="summary-title">
        <h2 id="summary-title">{pick(title, view.locale)}</h2>
        <SummaryText text={rewriting ? '' : cp.summary} />
        {rewriting && <p class="muted">{t('writing')}</p>}
      </section>
      {error && (
        <p class="error" role="alert">
          {t(error)}
        </p>
      )}
      {correcting ? (
        <form
          class="correction"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!correction.trim()) return;
            setRewriting(true);
            const ok = await run({ type: 'correct', phase: cp.phase, text: correction.trim() });
            setRewriting(false);
            if (ok) {
              setCorrecting(false);
              setCorrection('');
            }
          }}
        >
          <label for="correction">{t('correctionLabel')}</label>
          <textarea id="correction" rows={3} value={correction} onInput={(e) => setCorrection((e.target as HTMLTextAreaElement).value)} />
          <div class="actions">
            <button type="submit" class="button-primary" disabled={busy || !correction.trim()}>
              {t('sendCorrection')}
            </button>
            <button type="button" class="button-secondary" disabled={busy} onClick={() => setCorrecting(false)}>
              {t('cancel')}
            </button>
          </div>
        </form>
      ) : (
        <div class="actions">
          <button type="button" class="button-primary" disabled={busy} onClick={() => void run({ type: 'confirm', phase: cp.phase })}>
            {t('confirm')}
          </button>
          <button type="button" class="button-secondary" disabled={busy} onClick={() => setCorrecting(true)}>
            {t('correct')}
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * A measure asked inside the conversation, word for word with its options (ADR 0002). Nothing is chosen
 * until the participant chooses; an optional one can be skipped. Writing in the box instead also works.
 */
function MeasureInChat({ widget, locale, t, onAnswer, onCancel }: { widget: Extract<NonNullable<SessionView['messages'][number]['widget']>, { type: 'measure' }>; locale: string; t: ReturnType<typeof translator>; onAnswer: (v: Value | null) => void; onCancel?: () => void }) {
  // A first showing has nothing chosen; shown again, the current answer is selected (ADR 0002).
  const [value, setValue] = useState<Value | undefined>(widget.reopen && widget.current !== null ? widget.current : undefined);
  const form = useRef<HTMLFormElement>(null);
  // Opened by the participant (a follow-up's answer to change), it takes focus: on the answer they have now.
  useEffect(() => {
    if (onCancel) (form.current?.querySelector<HTMLInputElement>('input:checked') ?? form.current?.querySelector<HTMLInputElement>('input'))?.focus();
  }, []);
  return (
    <form
      ref={form}
      class="measure-in-chat"
      onSubmit={(e) => {
        e.preventDefault();
        if (value !== undefined) onAnswer(value);
      }}
    >
      {widget.reopen && <p class="muted">{t('measureReopened')}</p>}
      <MeasureField q={widget.question} value={value} locale={locale} t={t} onChange={setValue} />
      <div class="actions">
        <button type="submit" class="button-primary" disabled={value === undefined}>
          {t('pulseSubmit')}
        </button>
        {!widget.required && (
          <button type="button" class="button-secondary" onClick={() => onAnswer(null)}>
            {t('skipPage')}
          </button>
        )}
        {onCancel && (
          <button type="button" class="button-secondary" onClick={onCancel}>
            {t('cancel')}
          </button>
        )}
      </div>
    </form>
  );
}

/** Summaries arrive as "- " bullet lines; render them as a real list. */
export function SummaryText({ text }: { text: string }) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const bullets = lines.filter((l) => /^[-*•]\s/.test(l));
  if (bullets.length === 0) return <p>{text}</p>;
  return (
    <>
      {lines.filter((l) => !/^[-*•]\s/.test(l)).map((l) => <p key={l}>{l}</p>)}
      <ul>
        {bullets.map((l) => (
          <li key={l}>{l.replace(/^[-*•]\s/, '')}</li>
        ))}
      </ul>
    </>
  );
}

/**
 * Continue later, as a modal dialog over the conversation (visual draft). The native <dialog> keeps
 * focus inside and closes on Escape; where showModal is missing (older browsers, jsdom) it opens inline.
 */
function PausePanel({ t, resumeUrl, personal, onClose }: { t: ReturnType<typeof translator>; resumeUrl: string | null; personal: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const d = dialog.current!;
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
    heading.current?.focus();
  }, []);
  return (
    <dialog class="panel pause-dialog" ref={dialog} aria-labelledby="pause-title" onClose={onClose}>
      <h2 id="pause-title" ref={heading} tabIndex={-1}>
        {t('pauseTitle')}
      </h2>
      <p>{personal ? t('pausePersonal') : resumeUrl ? t('pauseShared') : t('pauseLost')}</p>
      {resumeUrl && (
        <>
          <input class="resume-url" readOnly value={resumeUrl} aria-label={t('copyLink')} onFocus={(e) => (e.target as HTMLInputElement).select()} />
          <button
            type="button"
            class="button-secondary"
            onClick={() => void navigator.clipboard?.writeText(resumeUrl).then(() => setCopied(true))}
          >
            {copied ? t('copied') : t('copyLink')}
          </button>
        </>
      )}
      <button type="button" class="button-secondary" onClick={onClose}>
        {t('close')}
      </button>
    </dialog>
  );
}

/**
 * What a follow-up refers to: the measure and the participant's own answer, with a way to change it.
 * Saying so in the box does not work: with one follow-up question the reply is not read before the next
 * one, and the model has no tools here (MEASURED 2026-11-02).
 */
function FollowUpContext({ view, t, onChange }: { view: SessionView; t: ReturnType<typeof translator>; onChange?: () => void }) {
  const { questionId, answer } = view.step!.followUp!;
  const q = view.step!.questions.find((x) => x.id === questionId);
  if (!q) return null;
  const optionLabel = (v: string) => ('options' in q ? pick(q.options.find((o) => o.value === v)?.labels, view.locale) || v : v);
  const shown =
    q.type === 'scale' && typeof answer === 'number'
      ? `${answer}${answer === q.scale.min ? ` (${pick(q.scale.minLabel, view.locale)})` : answer === q.scale.max ? ` (${pick(q.scale.maxLabel, view.locale)})` : ''}`
      : Array.isArray(answer)
        ? answer.map(optionLabel).join(', ')
        : optionLabel(String(answer));
  return (
    <p class="followup-context">
      {t('followUpContext', { question: pick(q.text, view.locale), answer: shown })}
      {onChange && (
        <>
          {' '}
          <button type="button" id="change-answer" class="link-button" onClick={onChange}>
            {t('changeAnswer')}
          </button>
        </>
      )}
    </p>
  );
}

/**
 * When the round opened, in the survey's language: the month alone for monthly rounds ("oktober 2026" is
 * too long for the header), day and month for rounds every n days ("25 oktober").
 */
export function roundDate(round: { opensOn: string; monthly: boolean }, locale: string) {
  const options: Intl.DateTimeFormatOptions = round.monthly ? { month: 'long', timeZone: 'UTC' } : { day: 'numeric', month: 'long', timeZone: 'UTC' };
  return new Intl.DateTimeFormat(locale, options).format(new Date(`${round.opensOn}T00:00:00Z`));
}

function EndPanel({ title, body, trust }: { title: string; body: string; trust: { href: string; text: string } }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  return (
    <main class="end">
      <h1 ref={heading} tabIndex={-1}>
        {title}
      </h1>
      <p>{body}</p>
      <p>
        <a href={trust.href}>{trust.text}</a>
      </p>
    </main>
  );
}
